import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import CloseOutlined from "@mui/icons-material/CloseOutlined";

import {
  Autocomplete,
  Dialog,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import "./BroadcastContact.css";
import AdditionalFilter from "../Campaign/AdditionalFilter";
import BroadcastDialog from "../Campaign/BroadcastDialog";
import "../Components/Components.css";
import Loader from "../Components/Loader";

import {
  getCustomer,
  getDataSource,
  getTableByDataSource,
} from "../Store/Actions/ContactAction";

const BroadcastContact = ({
  addRecipient,
  currentTableID,
  currentTableName,
  isAddingInProgress,
  isAddingRecipients,
  isOpen,
  setIsBroadcastContactShown,
}) => {
  const [name, setName] = useState("");
  const [ref, setRef] = useState("");
  const [keyword, setKeyword] = useState("");
  const [sort, setSort] = useState("ASC");
  const [contact, setContact] = useState([]); // The actual contacts
  const [contactPhone, setContactPhone] = useState([]);
  const [contactsToBroadcast, setContactsToBroadcast] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [dataSources, setDataSources] = useState([]);

  const initialDisplayValues = {
    dataTableID: null,
  };

  // Display values for autocomplete components
  const [displayValues, setDisplayValues] = useState(initialDisplayValues);

  const [isAdditionalFilterDialogShown, setIsAdditionalFilterDialogShown] =
    useState(false);

  const [isBroadcastDialogShown, setIsBroadcastDialogShown] = useState(false);
  const [isDataSourcesLoading, setIsDataSourcesLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [rowCountState, setRowCountState] = useState(0);
  const [selectedDataSourceID, setSelectedDataSourceID] = useState(null);

  const [selectedDataTableID, setSelectedDataTableID] = useState(
    currentTableID ?? null
  );

  const [selectedDataTableName, setSelectedDataTableName] = useState(
    currentTableName ?? ""
  );

  const [tables, setTables] = useState([]);

  useEffect(() => {
    if (isOpen) {
      handleReset();

      if (isAddingRecipients) {
        setIsDataSourcesLoading(false);
        fetchCustomer(null, null, null, null, null, currentTableID);
      } else {
        setIsDataSourcesLoading(true);

        getDataSource()
          .then((response) => {
            if (response.status) {
              setDataSources(response.data);
              setIsDataSourcesLoading(false);
            } else {
              console.log("error", response.error);
              setIsDataSourcesLoading(false);
              toast.error("error getting data source", { autoClose: 3000 });
            }
          })
          .catch((err) => {
            console.log("err", err);
            setIsDataSourcesLoading(false);
            toast.error("error getting data source", { autoClose: 3000 });
          });
      }
    }
  }, [isOpen]);

  const columns = [
    {
      field: "ContactID",
      flex: 1,
      headerName: "ID",
    },

    {
      field: "ContactRef",
      flex: 1,
      headerName: "ContactRef",
    },

    {
      field: "FirstName",
      flex: 1,
      headerName: "Name",

      renderCell: (params) =>
        (!params.row.FirstName || params.row?.FirstName?.trim().length === 0) &&
        !params.row.LastName // Show "Unknown Contact" if FirstName and LastName is empty
          ? "Unknown Contact"
          : params.row.FirstName +
            " " +
            (params.row.LastName ? params.row.LastName : ""),
    },

    {
      field: "PhoneNumber",
      flex: 1,
      headerName: "Phone Number",

      renderCell: (params) =>
        contactPhone?.filter(
          (phone) => phone.ContactID === params.row.ContactID
        )[0]?.PhoneNo,
    },

    {
      field: "lastCall",
      flex: 1,
      headerName: "Last Call",
    },

    {
      field: "status",
      flex: 1,
      headerName: "Status",
    },

    {
      field: "assignedOn",
      flex: 1,
      headerName: "Assigned On",
    },

    {
      field: "assignedTo",
      flex: 1,
      headerName: "Assigned To",
    },
  ];

  const fetchCustomer = (
    keyword,
    page = 1,
    sort = "ASC",
    name,
    ref,
    tableID
  ) => {
    setIsLoading(true);

    getCustomer(keyword, page, sort, name, ref, tableID)
      .then((response) => {
        if (response.status) {
          setContact(response.data.customers);

          setContactPhone(
            response.data.customers_phone.filter(
              (phone) => phone.PhoneTypeID === 9
            )
          ); // Only get the Mobile type

          setRowCountState(response.data.total);
          setIsLoading(false);
        } else {
          console.log("error", response.error);
          setIsLoading(false);
          toast.error("error getting customers", { autoClose: 3000 });
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsLoading(false);
        toast.error("error getting customers", { autoClose: 3000 });
      });
  };

  const handleClose = () => {
    handleReset();
    setIsBroadcastContactShown(false);
  };

  const handleReset = () => {
    setName("");
    setRef("");
    setKeyword("");
    setSort("ASC");
    setContactsToBroadcast([]);
    setSelectedDataSourceID(null);

    if (!isAddingRecipients) {
      setSelectedDataTableID(null);
      setSelectedDataTableName("");
      setDisplayValues(initialDisplayValues);
    }
  };

  return (
    <Dialog
      open={isOpen}
      className="dialog broadcast-contact"
      fullWidth
      maxWidth={selectedDataTableID ? "xl" : "xs"}
      scroll="paper"
      onClose={handleClose}
    >
      <div className="dialog-container">
        <div className="dialog-content">
          <IconButton className="close-button" onClick={handleClose}>
            <CloseOutlined />
          </IconButton>

          <div className="title">
            <h2>
              {isAddingRecipients
                ? `Add Recipients - ${selectedDataTableName}`
                : selectedDataTableName
                ? `Broadcast WA - ${selectedDataTableName}`
                : "Broadcast WA"}
            </h2>
          </div>

          {isDataSourcesLoading ? (
            <Loader />
          ) : selectedDataTableID ? (
            <div className="container">
              <div className="container-header">
                <div className="search">
                  <div className="row">
                    <TextField
                      className="search-item"
                      label="Contact Name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />

                    <TextField
                      className="search-item"
                      label="Contact Ref"
                      value={ref}
                      onChange={(e) => setRef(e.target.value)}
                    />

                    <section className="broadcast-button-container">
                      <div className="single_button">
                        <Tooltip title="Clear" arrow>
                          <IconButton
                            size="medium"
                            onClick={() => {
                              handleReset();
                            }}
                          >
                            <Icon height="26" icon="ri:brush-3-line" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </section>
                  </div>

                  <div className="row">
                    <TextField
                      className="search-item"
                      label="Keyword"
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                    />

                    <TextField
                      className="search-item"
                      label="Sort"
                      select
                      value={sort}
                      onChange={(e) => setSort(e.target.value)}
                    >
                      <MenuItem value="ASC">Ascending</MenuItem>
                      <MenuItem value="DESC">Descending</MenuItem>
                    </TextField>

                    <p className="selected-contact-count">
                      {contactsToBroadcast.length} Contacts Selected
                    </p>
                  </div>
                </div>

                <div className="search-buttons">
                  <button
                    className="search-button"
                    onClick={() => fetchCustomer(keyword, 1, sort, name, ref)}
                  >
                    <Icon icon="mingcute:search-line" width="20" height="20" />
                    Search
                  </button>

                  <button
                    className="search-button"
                    disabled={
                      contactsToBroadcast.length <= 0 || isAddingInProgress
                    }
                    onClick={() => {
                      isAddingRecipients
                        ? addRecipient(contactsToBroadcast)
                        : setIsBroadcastDialogShown(true);
                    }}
                  >
                    <Icon
                      icon={
                        isAddingRecipients
                          ? "material-symbols:add-rounded"
                          : "fluent:megaphone-loud-32-filled"
                      }
                      width="20"
                      height="20"
                    />
                    {isAddingRecipients ? "Add" : "Broadcast"}
                  </button>
                </div>
              </div>

              <DataGrid
                autoPageSize
                checkboxSelection
                className="table"
                columns={columns}
                disableColumnFilter
                disableColumnMenu
                disableSelectionOnClick
                keepNonExistentRowsSelected
                loading={isLoading}
                page={currentPage}
                pageSize={20}
                paginationMode="server"
                rowCount={rowCountState}
                rows={contact}
                rowsPerPageOptions={[]} // hide rows per page
                selectionModel={contactsToBroadcast}
                getRowId={
                  (row) =>
                    `${row.ContactID}-${contactPhone
                      ?.filter((phone) => phone.ContactID === row.ContactID)[0]
                      ?.PhoneNo.replace(/\D/g, "")}-${row.DataTableRecID}` // Filter the numbers
                }
                onPageChange={(newPage) => {
                  fetchCustomer(keyword, newPage + 1, sort, name, ref);
                  setCurrentPage(newPage);
                }}
                onSelectionModelChange={(newContactsToBroadcast) =>
                  setContactsToBroadcast(newContactsToBroadcast)
                }
              />
            </div>
          ) : (
            <div className="select-data-source">
              <p>Select data source</p>

              <TextField
                className="search-item"
                label="Data Source"
                select
                value={selectedDataSourceID}
                onChange={(e) => {
                  setSelectedDataSourceID(e.target.value);

                  getTableByDataSource(e.target.value)
                    .then((response) => {
                      if (response.status) {
                        setTables(response.data);
                      } else {
                        console.log("error", response.error);

                        toast.error("error getting tables", {
                          autoClose: 3000,
                        });
                      }
                    })
                    .catch((err) => {
                      console.log("err", err);

                      toast.error("error getting data source", {
                        autoClose: 3000,
                      });
                    });
                }}
              >
                {dataSources.map((dataSource) => (
                  <MenuItem
                    key={dataSource.DataSourceID}
                    value={dataSource.DataSourceID}
                  >
                    {dataSource.DataSourceName}
                  </MenuItem>
                ))}
              </TextField>

              {/* <TextField
                label="Table"
                select
                value={selectedDataTableID}
                onChange={(e) => {
                  setSelectedDataTableID(e.target.value);
                  fetchCustomer(keyword, 1, sort, name, ref, e.target.value);
                }}
              >
                {tables ? (
                  tables.map((table) => {
                    return (
                      <MenuItem
                        key={table.DataTableID}
                        value={table.DataTableID}
                        onClick={() =>
                          setSelectedDataTableName(table.DataTableName)
                        }
                      >
                        {table.DataTableName}
                      </MenuItem>
                    );
                  })
                ) : (
                  <MenuItem value="0" disabled>
                    Select data source
                  </MenuItem>
                )}
              </TextField> */}

              <Autocomplete
                className="search-item status-selector"
                disableClearable
                options={tables}
                value={displayValues.dataTableID}
                getOptionLabel={(table) => table.DataTableName}
                onChange={(event, value) => {
                  setSelectedDataTableID(value.DataTableID);
                  fetchCustomer(keyword, 1, sort, name, ref, value.DataTableID);

                  setDisplayValues({
                    ...displayValues,
                    dataTableID: value,
                  });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Table" />
                )}
              />
            </div>
          )}
        </div>
      </div>

      <AdditionalFilter
        isOpen={isAdditionalFilterDialogShown}
        setIsAdditionalFilterDialogShown={setIsAdditionalFilterDialogShown}
      />

      <BroadcastDialog
        closeBroadcastContact={handleClose}
        dataTableID={selectedDataTableID}
        dataTableName={selectedDataTableName}
        isOpen={isBroadcastDialogShown}
        setIsBroadcastDialogShown={setIsBroadcastDialogShown}
        selectedContacts={contactsToBroadcast}
      />
    </Dialog>
  );
};

export default BroadcastContact;
